import { Trans, useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import {
  InputLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  typographyVariants,
} from '@mint-lib/tw-components';
import { FileUploader } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { type CommonPropsType } from './ModalViewFactory.jsx';

type FileUploadViewProps = Pick<
  CommonPropsType,
  | 'fileUploaderRef'
  | 'setFiles'
  | 'handleFileUpload'
  | 'activities'
  | 'selectedActivityId'
  | 'onActivityChange'
>;

const ACCEPTED_FILE_FORMATS = ['.xlsx'];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export const FileUploadView = (props: FileUploadViewProps) => {
  const { t } = useTranslation('@myn/planning_v2');
  const ff = useFlags('@mint/arm', ['disableActivityLevel']);
  const {
    fileUploaderRef,
    setFiles,
    handleFileUpload,
    activities,
    selectedActivityId,
    onActivityChange,
  } = props;

  const fileUploader = (
    <FileUploader
      ref={fileUploaderRef}
      accept={ACCEPTED_FILE_FORMATS}
      totalSize={MAX_FILE_SIZE}
      fullWidth
      onChange={setFiles}
      onUpload={handleFileUpload}
      maxFiles={1}
      itemVariant="medium"
    />
  );

  if (!ff.disableActivityLevel.enabled) {
    return (
      <>
        <Trans
          i18nKey="<Typography><bold>Upload your activity</bold> in Excel format. <br/> Make sure it <bold>follows the structure</bold> of the current template - you can download it below if needed.</Typography>"
          t={t}
          components={{
            Typography: (
              <span
                className={cn(
                  typographyVariants({ variant: 'bodyLong01' }),
                  'mb-6',
                )}
              />
            ),
            bold: <span className="font-bold" />,
          }}
        />
        {fileUploader}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Trans
        i18nKey="<Typography><bold>Select an existing activity and upload data</bold> in Excel format. Make sure it <bold>follows the structure</bold> of the current template - you can download it below if needed.</Typography>"
        t={t}
        components={{
          Typography: (
            <span
              className={cn(typographyVariants({ variant: 'bodyLong01' }))}
            />
          ),
          bold: <span className="font-bold" />,
        }}
      />

      <div>
        <InputLabel htmlFor="activity-select" label={t('Activity')} />
        <Select
          value={selectedActivityId}
          onValueChange={onActivityChange}
          name="Activity"
        >
          <SelectTrigger id="activity-select" size="medium" className="w-full">
            <SelectValue placeholder={t('Select activity')} />
          </SelectTrigger>
          <SelectContent>
            {activities.map((activityItem) => {
              const hasFlights = activityItem.subItems.length > 0;
              const isSelectable = hasFlights && !!activityItem.activityId;

              return (
                <SelectItem
                  key={activityItem.mediaDataId}
                  value={activityItem.activityId ?? activityItem.mediaDataId}
                  disabled={!isSelectable}
                >
                  {activityItem.name}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {fileUploader}
    </div>
  );
};
